import { runInAction } from 'mobx';
import { MobXProviderContext, observer } from 'mobx-react';
import { IPromiseBasedObservable } from 'mobx-utils';
import { ContactModelV2 } from 'Models/ContactModelV2';
import * as React from 'react';
import { Loader, Message } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import {
  formatNumberNoPlusIfUS,
  formatNumberWithNationalCode,
} from 'Utils/phoneUtil';
import { ChatListItemTextRendererProps } from './ChatListItemTextRenderer.types';
import { ChatListItemText } from './index';

const ChatListItemTextRendererObserver: React.FC<
  ChatListItemTextRendererProps
> = ({ conversation, from }) => {
  const {
    ui: uiStore,
    contact: contactStore,
    conversation: conversationStore,
    person: personStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  if (from === 'group-info') return null;

  let listOfContactsPromises: IPromiseBasedObservable<ContactModelV2[]>[];
  let isUnread = false;
  if (
    uiStore.selectConversationUnreadCounts(conversation.id).unreadMessages > 0
  ) {
    isUnread = true;
  }

  if (
    conversation?.grouping === 'Channel' ||
    conversation?.grouping === 'Group'
  ) {
    if (
      (conversation.topic?.includes('Group') || !conversation.topic) &&
      conversation.grouping === 'Group'
    ) {
      runInAction(
        () =>
          (conversation.topic = conversationStore.createProperName(
            conversation.participants
          ))
      );
      listOfContactsPromises = conversation.participants.map((participant) =>
        personStore.getAllContactsWithSameNum(participant.phone)
      );
      return (
        <ChatListItemText
          key={`chat-list-item-${conversation.id}`}
          bold={isUnread}
          title={listOfContactsPromises.map(
            (listOfContactsPbo) =>
              listOfContactsPbo?.case({
                pending: () => <Loader indeterminate active size="tiny" />,
                fulfilled: () => (
                  <>
                    {conversationStore.createProperName(
                      conversation.participants
                    )}
                  </>
                ),
              }) || <>{conversation.topic}</>
          )}
        />
      );
    }

    return (
      <ChatListItemText
        key={`chat-list-item-${conversation.id}`}
        bold={isUnread}
        title={conversation.topic}
      />
    );
  }

  if (conversation?.grouping === 'OneOnOne') {
    const otherPtc = conversation?.participants.find(
      ({ personId }) => personId !== personStore.loggedInPersonId
    );
    const mySelfPtc = conversation?.participants.find(
      ({ personId }) => personId === personStore.loggedInPersonId
    );

    if (otherPtc) {
      if (otherPtc.phone) {
        const phoneNumNoPlus = formatNumberNoPlusIfUS(otherPtc.phone);
        const phoneNumWithNationalCode = formatNumberWithNationalCode(
          otherPtc.phone
        );

        const otherPtcContact =
          contactStore.getContactByPhoneNumber(phoneNumNoPlus);
        if (otherPtcContact) {
          const extrContact = personStore.getExtrContactByPhoneNumber(
            otherPtcContact.phoneNumber
          );
          const displayName = extrContact?.name || otherPtcContact?.DisplayName;

          if (!extrContact) {
            const extrContactPbo =
              personStore.getAllContactsWithSameNum(phoneNumNoPlus);

            return extrContactPbo?.case({
              pending: () => <Loader indeterminate active size="tiny" />,
              fulfilled: (contacts) => {
                const contact = contacts[0];

                return (
                  <ChatListItemText
                    key={`chat-list-item-${conversation.id}`}
                    bold={isUnread}
                    title={
                      contact?.name ||
                      phoneNumWithNationalCode ||
                      displayName ||
                      conversation.topic
                    }
                    subtitle="[SMS]"
                  />
                );
              },
            });
          }

          return (
            <ChatListItemText
              key={`chat-list-item-${conversation.id}`}
              bold={isUnread}
              title={
                displayName || phoneNumWithNationalCode || conversation.topic
              }
              subtitle="[SMS]"
            />
          );
        }

        const extrContactsPbo =
          personStore.getAllContactsWithSameNum(phoneNumNoPlus);
        if (!extrContactsPbo) {
          return (
            <ChatListItemText
              key={`chat-list-item-${conversation.id}`}
              bold={isUnread}
              title={phoneNumWithNationalCode}
            />
          );
        }

        return extrContactsPbo.case({
          pending: () => <Loader indeterminate active size="tiny" />,
          fulfilled: (contacts) => {
            const contact = contacts[0];

            return (
              <ChatListItemText
                key={`chat-list-item-${conversation.id}`}
                bold={isUnread}
                title={contact?.name || phoneNumWithNationalCode}
              />
            );
          },
        });
      }

      if (isFinite(otherPtc.personId)) {
        const otherPtcPerson = personStore.loadPersonByIdGetIfMissingGet(
          otherPtc.personId.toString()
        );
        if (!otherPtcPerson) return <Loader indeterminate active size="tiny" />;

        return otherPtcPerson.case({
          fulfilled: (response) => {
            // Worst case scenario we don't know the name
            const displayName =
              response?.firstName && response?.lastName
                ? `${response.firstName} ${response.lastName}`
                : conversation.topic;
            const emoji = uiStore.selectPersonMessageStatus(response.sourceId);

            return (
              <ChatListItemText
                key={`chat-list-item-${conversation.id}-${emoji?.title}`}
                bold={isUnread}
                title={response?.name || displayName}
                {...{ emoji }}
              />
            );
          },
          pending: () => <Loader indeterminate active size="tiny" />,
          rejected: () => (
            <div data-private className="display-name color-removed">
              Person {otherPtc.personId}
            </div>
          ),
        });
      }

      return (
        <Message
          visible
          error
          content={
            'Error: Empty personId and phone for Participant ' + otherPtc.id
          }
        />
      );
    }

    if (mySelfPtc && isFinite(mySelfPtc.personId)) {
      const mySelfPtcPerson = personStore.loadPersonByIdGetIfMissingGet(
        mySelfPtc.personId.toString()
      );
      if (!mySelfPtcPerson) return <Loader indeterminate active size="tiny" />;

      return mySelfPtcPerson.case({
        fulfilled: (response) => {
          // Worst case scenario we don't know the name
          const displayName = response?.name || conversation.topic;
          const emoji = uiStore.selectPersonMessageStatus(response.sourceId);

          return (
            <ChatListItemText
              key={`chat-list-item-${conversation.id}-${emoji?.title}`}
              bold={isUnread}
              title={displayName}
              {...{ emoji, isOwnChat: true }}
            />
          );
        },
        pending: () => <Loader indeterminate active size="tiny" />,
        rejected: () => (
          <div data-private className="display-name color-removed">
            Person {mySelfPtc.personId}
          </div>
        ),
      });
    }

    return <Message visible error content="Other Participant not found!" />;
  }

  return null;
};

export const ChatListItemTextRenderer = observer(
  ChatListItemTextRendererObserver
);
